import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Divider, IconButton,
  Tabs, Tab, Snackbar, Alert, Tooltip, Paper,
} from '@mui/material';
import PaletteIcon        from '@mui/icons-material/Palette';
import TextFieldsIcon     from '@mui/icons-material/TextFields';
import RoundedCornerIcon  from '@mui/icons-material/RoundedCorner';
import RestoreIcon        from '@mui/icons-material/Restore';
import FileDownloadIcon   from '@mui/icons-material/FileDownload';
import FileUploadIcon     from '@mui/icons-material/FileUpload';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import ShoppingCartIcon   from '@mui/icons-material/ShoppingCart';
import ArticleIcon        from '@mui/icons-material/Article';
import { ThemeContext }   from '../../context/themeContext';
import { AuthContext }    from '../../context/authContext';
 
/* ─── Color Picker ─────────────────────────────────────────────────────────── */
const ColorInput = ({ label, value, onChange, description }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <Box sx={{ position: 'relative' }}>
      <input type="color" value={value?.startsWith('#') ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 40, height: 40, border: 'none', borderRadius: 10, cursor: 'pointer', padding: 0, background: 'none' }} />
      <Box sx={{ position: 'absolute', inset: 0, borderRadius: '10px', border: '2px solid var(--border)', pointerEvents: 'none' }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{label}</Typography>
      {description && <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{description}</Typography>}
    </Box>
    <TextField size="small" value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem', height: 32 } }} />
  </Box>
);

/* ─── Font Selector ────────────────────────────────────────────────────────── */
const FONT_OPTIONS = [
  "'Plus Jakarta Sans', sans-serif",
  "'DM Sans', sans-serif",
  "'Space Grotesk', monospace",
  "'Poppins', sans-serif",
  "'Outfit', sans-serif",
  "'Nunito', sans-serif",
  "'Montserrat', sans-serif",
  "'Raleway', sans-serif",
  "'Lato', sans-serif",
  "'Open Sans', sans-serif",
  "'Roboto', sans-serif",
  "'Inter', sans-serif",
];

const FontSelect = ({ label, value, onChange }) => (
  <Box sx={{ py: 1 }}>
    <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', mb: 0.5 }}>{label}</Typography>
    <TextField select fullWidth size="small" value={value || ''} onChange={(e) => onChange(e.target.value)}
      SelectProps={{ native: true }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' } }}>
      {FONT_OPTIONS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f.replace(/'/g, '').split(',')[0]}</option>)}
    </TextField>
    <Typography sx={{ fontFamily: value, fontSize: '0.9rem', mt: 0.5, color: 'var(--text-secondary)' }}>
      Vista previa de la fuente seleccionada
    </Typography>
  </Box>
);

/* ─── Radius Slider ────────────────────────────────────────────────────────── */
const RadiusInput = ({ label, value, onChange }) => (
  <Box sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', minWidth: 90 }}>{label}</Typography>
    <input type="range" min="0" max="30" value={parseInt(value) || 0}
      onChange={(e) => onChange(`${e.target.value}px`)}
      style={{ flex: 1, accentColor: 'var(--accent)' }} />
    <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: 36 }}>{value}</Typography>
    <Box sx={{ width: 36, height: 36, backgroundColor: 'var(--accent)', borderRadius: value, transition: 'border-radius 0.3s ease', flexShrink: 0 }} />
  </Box>
);

/* ─── Preview Components ───────────────────────────────────────────────────── */
const PreviewNavbar = () => (
  <Box sx={{ bgcolor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ width: 60, height: 22, bgcolor: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)' }} />
    <Box sx={{ flex: 1, height: 30, bgcolor: 'var(--bg-subtle)', borderRadius: 'var(--radius-full)' }} />
    <Box sx={{ display: 'flex', gap: 1 }}>
      {[false, true, false].map((active, i) => (
        <Box key={i} sx={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', bgcolor: active ? 'var(--accent-soft)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: active ? 'var(--accent)' : 'var(--text-muted)' }} />
        </Box>
      ))}
    </Box>
  </Box>
);

const PreviewCard = () => (
  <Box sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', bgcolor: 'var(--bg-card)', maxWidth: 200 }}>
    <Box sx={{ height: 100, bgcolor: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <ShoppingCartIcon sx={{ fontSize: 36, color: 'var(--text-muted)', opacity: 0.4 }} />
    </Box>
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>CATEGORÍA</Typography>
      <Typography sx={{ fontFamily: 'var(--font-heading)', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', my: 0.4 }}>Producto ejemplo</Typography>
      <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)' }}>COD ABC123</Typography>
      <Typography sx={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 800, color: 'var(--success)', mt: 0.75 }}>$1.234,56</Typography>
      <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--price-old)', textDecoration: 'line-through' }}>$1.800,00</Typography>
      <Box sx={{ display: 'inline-flex', mt: 0.5, px: 1, py: 0.2, bgcolor: 'var(--success)', color: '#fff', borderRadius: 'var(--radius-full)', fontSize: '0.58rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>Oferta</Box>
      <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
        <Box sx={{ flex: 1, height: 26, bgcolor: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }} />
        <Box sx={{ width: 30, height: 26, bgcolor: 'var(--accent)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShoppingCartIcon sx={{ fontSize: 13, color: '#fff' }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

const PreviewBadges = () => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    {[
      { label: 'Oferta',    bg: 'var(--success)' },
      { label: 'Lista',     bg: 'var(--purple)'  },
      { label: 'Disponible',bg: 'var(--info)'    },
      { label: 'Últimas u.',bg: 'var(--warning)' },
      { label: 'Sin Stock', bg: 'var(--text-muted)' },
    ].map(b => (
      <Box key={b.label} sx={{ px: 1.25, py: 0.4, bgcolor: b.bg, color: '#fff', borderRadius: 'var(--radius-full)', fontSize: '0.68rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>{b.label}</Box>
    ))}
  </Box>
);

/* ─── MAIN ─────────────────────────────────────────────────────────────────── */
const ThemeAdmin = () => {
  const { themeConfig, saveTheme, resetTheme, exportTheme, importTheme, DEFAULT_THEME } = useContext(ThemeContext);
  const { empresa, user } = useContext(AuthContext);

  const [tab,   setTab]   = useState(0);
  const [local, setLocal] = useState(themeConfig);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const fileRef = useRef(null);

  useEffect(() => { setLocal(themeConfig); }, [themeConfig]);

  // Actualiza un campo, aplica en vivo y guarda en DB
  const update = (key, val) => {
    const next = { ...local, [key]: val };
    setLocal(next);
    saveTheme(next);
  };

  const handleReset = () => {
    resetTheme();
    setLocal(DEFAULT_THEME);
    setSnack({ open: true, msg: 'Tema restaurado a valores por defecto', sev: 'info' });
  };

  const handleExport = () => {
    exportTheme();
    setSnack({ open: true, msg: 'Tema exportado correctamente', sev: 'success' });
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const ok = importTheme(ev.target.result);
      setSnack({ open: true, msg: ok ? 'Tema importado correctamente' : 'JSON inválido', sev: ok ? 'success' : 'error' });
      if (ok) setLocal(JSON.parse(ev.target.result));
    };
    reader.readAsText(file);
  };

  if (user?.rol_id !== 1) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <Typography sx={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', color: 'var(--text-muted)' }}>
        No tenés permisos para acceder a esta sección.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 'var(--container-max)', mx: 'auto', px: { xs: 2, md: 3 }, py: 3, animation: 'fadeInUp 0.5s ease both' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg, var(--accent), var(--primary))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-glow)' }}>
            <PaletteIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.4rem', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Personalizar Tema
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {empresa?.nombre_sitio || 'Empresa'} — Cambios en tiempo real
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Importar JSON">
            <IconButton onClick={() => fileRef.current?.click()} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              <FileUploadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Exportar JSON">
            <IconButton onClick={handleExport} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              <FileDownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Restaurar por defecto">
            <IconButton onClick={handleReset} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              <RestoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <input ref={fileRef} type="file" accept=".json" hidden onChange={handleImport} />
        </Box>
      </Box>

      <Grid container spacing={3}>

        {/* LEFT: Controles */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: '1px solid var(--border)', '& .MuiTab-root': { fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', minHeight: 48 }, '& .Mui-selected': { color: 'var(--accent) !important' }, '& .MuiTabs-indicator': { backgroundColor: 'var(--accent)' } }}>
              <Tab icon={<PaletteIcon      sx={{ fontSize: 17 }} />} iconPosition="start" label="Colores"    />
              <Tab icon={<TextFieldsIcon   sx={{ fontSize: 17 }} />} iconPosition="start" label="Tipografía" />
              <Tab icon={<RoundedCornerIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Bordes"    />
              <Tab icon={<ArticleIcon      sx={{ fontSize: 17 }} />} iconPosition="start" label="Footer"     />
            </Tabs>

            <Box sx={{ p: { xs: 2, md: 3 }, maxHeight: '65vh', overflowY: 'auto' }}>

              {/* TAB 0 — Colores (usa snake_case igual que la DB) */}
              {tab === 0 && (<>
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>Marca</Typography>
                <ColorInput label="Accent"        value={local.accent}        onChange={v => update('accent', v)}        description="Botones, links, badges activos" />
                <ColorInput label="Accent hover"  value={local.accent_hover}  onChange={v => update('accent_hover', v)}  description="Hover del accent" />
                <ColorInput label="Color primario" value={local.primary_color} onChange={v => update('primary_color', v)} description="Navbar fondo, textos fuertes" />
                <ColorInput label="Primario claro" value={local.primary_light} onChange={v => update('primary_light', v)} />

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>Fondos</Typography>
                <ColorInput label="Fondo principal" value={local.bg_main}     onChange={v => update('bg_main', v)}     description="Fondo de toda la página" />
                <ColorInput label="Fondo cards"     value={local.bg_card}     onChange={v => update('bg_card', v)}     description="Cards, modales, navbar" />
                <ColorInput label="Fondo elevado"   value={local.bg_elevated} onChange={v => update('bg_elevated', v)} />
                <ColorInput label="Fondo sutil"     value={local.bg_subtle}   onChange={v => update('bg_subtle', v)}   description="Inputs, filtros, hover" />

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>Textos</Typography>
                <ColorInput label="Texto principal"   value={local.text_primary}   onChange={v => update('text_primary', v)}   />
                <ColorInput label="Texto secundario"  value={local.text_secondary} onChange={v => update('text_secondary', v)} />
                <ColorInput label="Texto muted"       value={local.text_muted}     onChange={v => update('text_muted', v)}     />
                <ColorInput label="Texto inverso"     value={local.text_inverse}   onChange={v => update('text_inverse', v)}   description="Texto sobre fondos oscuros" />

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>Bordes</Typography>
                <ColorInput label="Borde"       value={local.border_color} onChange={v => update('border_color', v)} />
                <ColorInput label="Borde hover" value={local.border_hover} onChange={v => update('border_hover', v)} />
                <ColorInput label="Divisor"     value={local.divider}      onChange={v => update('divider', v)}      />

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>Estados</Typography>
                <ColorInput label="Éxito / Oferta"   value={local.success_color} onChange={v => update('success_color', v)} description="Oferta, disponible, en carrito" />
                <ColorInput label="Advertencia"      value={local.warning_color} onChange={v => update('warning_color', v)} description="Últimas unidades" />
                <ColorInput label="Peligro / Error"  value={local.danger_color}  onChange={v => update('danger_color', v)}  description="Sin stock, errores" />
                <ColorInput label="Info / Disponible" value={local.info_color}   onChange={v => update('info_color', v)}    />
                <ColorInput label="Violeta / Lista"  value={local.purple_color}  onChange={v => update('purple_color', v)}  description="Precio de lista" />
              </>)}

              {/* TAB 1 — Tipografía */}
              {tab === 1 && (<>
                <FontSelect label="Fuente de títulos (headings)" value={local.font_heading} onChange={v => update('font_heading', v)} />
                <FontSelect label="Fuente de cuerpo (body)"      value={local.font_body}    onChange={v => update('font_body', v)}    />
                <FontSelect label="Fuente mono (códigos, precios)" value={local.font_mono}  onChange={v => update('font_mono', v)}    />
              </>)}

              {/* TAB 2 — Bordes */}
              {tab === 2 && (<>
                <RadiusInput label="Pequeño (sm)" value={local.radius_sm} onChange={v => update('radius_sm', v)} />
                <RadiusInput label="Mediano (md)" value={local.radius_md} onChange={v => update('radius_md', v)} />
                <RadiusInput label="Grande (lg)"  value={local.radius_lg} onChange={v => update('radius_lg', v)} />
                <RadiusInput label="Extra (xl)"   value={local.radius_xl} onChange={v => update('radius_xl', v)} />
              </>)}

              {/* TAB 3 — Footer */}
              {tab === 3 && (<>
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>Textos del Footer</Typography>
                <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: 'var(--text-muted)', mb: 2 }}>
                  Si dejás vacío, se usan los datos de la empresa.
                </Typography>
                <TextField fullWidth size="small" label="Copyright" value={local.footer_copyright || ''} onChange={e => update('footer_copyright', e.target.value)}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)' } }} />
                <TextField fullWidth size="small" label="Dirección" value={local.footer_address || ''} onChange={e => update('footer_address', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)' } }} />
              </>)}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: Preview */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 100 }}>
            <Paper sx={{ borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', p: { xs: 2, md: 3 }, bgcolor: 'var(--bg-main)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VisibilityIcon sx={{ color: 'var(--accent)', fontSize: 20 }} />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Vista Previa</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <PreviewNavbar />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['Categorías', 'Ofertas', 'Combos'].map((t, i) => (
                    <Box key={t} sx={{ px: 1.25, py: 0.4, borderRadius: 'var(--radius-full)', fontSize: '0.7rem', fontWeight: i === 0 ? 700 : 500, fontFamily: 'var(--font-body)', bgcolor: i === 0 ? 'var(--accent-soft)' : 'transparent', color: i === 0 ? 'var(--accent)' : 'var(--text-secondary)', border: i === 0 ? '1.5px solid var(--accent)' : '1.5px solid transparent' }}>{t}</Box>
                  ))}
                </Box>
                <PreviewCard />
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)' }}>Badges de estado:</Typography>
                <PreviewBadges />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" sx={{ borderRadius: 'var(--radius-md)', bgcolor: 'var(--accent)', fontFamily: 'var(--font-body)', fontWeight: 600, textTransform: 'none', fontSize: '0.78rem', '&:hover': { bgcolor: 'var(--accent-hover)' } }}>Botón Accent</Button>
                  <Button variant="outlined" size="small" sx={{ borderRadius: 'var(--radius-md)', borderColor: 'var(--border)', color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontWeight: 600, textTransform: 'none', fontSize: '0.78rem' }}>Outlined</Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ThemeAdmin;